'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './WelcomePanel.module.css';
import { Button } from './Button';

export interface WelcomePanelProfileProps {
  /** Profile image URL or emoji/icon */
  avatar?: React.ReactNode;
  /** User's display name */
  name: string;
  /** Company or organization name */
  company?: string;
  /** Job title or role */
  jobTitle?: string;
  /** Badge label (e.g., "Pro", "Level 2") */
  badge?: string;
  /** Badge icon — emoji or ReactNode (defaults to ⭐) */
  badgeIcon?: React.ReactNode;
  /** Link text for settings */
  settingsLinkText?: string;
  /** Settings link href */
  settingsHref?: string;
  /** Settings link click handler */
  onSettingsClick?: () => void;
}

export interface WelcomePanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Label text shown above the heading (e.g., "14 days left in trial") */
  label?: string;
  /** Optional second label shown inline with the first (e.g., "Team member") */
  secondLabel?: string;
  /** Main heading (e.g., "Welcome back, Sarah!") */
  heading: string;
  /** Description text below the heading */
  description?: string;
  /** Call-to-action button element */
  action?: React.ReactNode;
  /** Profile card data */
  profile?: WelcomePanelProfileProps;
  /** Additional class names */
  className?: string;
}

/**
 * WelcomePanel Component
 *
 * Dashboard hero card with gradient background, personalized greeting,
 * CTA button, and optional embedded profile card with gradient badge.
 *
 * Specifications:
 * - Background: linear-gradient(90deg, #13319A → #0B082F)
 * - Border radius: 32px (radius.xl)
 * - Padding: 40px (spacing.7)
 */
export const WelcomePanel = React.forwardRef<HTMLDivElement, WelcomePanelProps>(
  (
    {
      label,
      secondLabel,
      heading,
      description,
      action,
      profile,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(styles.panel, className)}
        {...props}
      >
        {/* Left Content */}
        <div className={styles.content}>
          {(label || secondLabel) && (
            <div className={styles.labels}>
              {label && <span className={styles.label}>{label}</span>}
              {secondLabel && <span className={styles.label}>{secondLabel}</span>}
            </div>
          )}
          <h1 className={styles.heading}>{heading}</h1>
          {description && (
            <p className={styles.description}>{description}</p>
          )}
          {action && (
            <div className={styles.action}>{action}</div>
          )}
        </div>

        {/* Right Profile Card */}
        {profile && (
          <div className={styles.profileCard}>
            <div className={styles.profileCardInner}>
            <div className={styles.avatarContainer}>
              <div className={styles.avatar}>
                {profile.avatar || '👤'}
              </div>
              {profile.badge && (
                <span className={styles.badge}>
                  <span className={styles.badgeIcon}>{profile.badgeIcon ?? '⭐'}</span> {profile.badge}
                </span>
              )}
            </div>
            <div className={styles.profileInfo}>
              {profile.badge && (
                <span className={styles.badgeCompact}>
                  <span className={styles.badgeIcon}>{profile.badgeIcon ?? '⭐'}</span> {profile.badge}
                </span>
              )}
              <h3 className={styles.profileName}>{profile.name}</h3>
              {(profile.company || profile.jobTitle) && (
                <div className={styles.profileDetails}>
                  {profile.company && (
                    <h4 className={styles.profileCompany}>{profile.company}</h4>
                  )}
                  {profile.jobTitle && (
                    <p className={styles.profileJobTitle}>{profile.jobTitle}</p>
                  )}
                </div>
              )}
              {(profile.settingsLinkText || profile.settingsHref || profile.onSettingsClick) && (
                <Button
                  variant="tertiary"
                  size="sm"
                  noPadding
                  onClick={profile.onSettingsClick}
                >
                  {profile.settingsLinkText || 'Update your settings'}
                </Button>
              )}
            </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

WelcomePanel.displayName = 'WelcomePanel';

export default WelcomePanel;
